import React from 'react';
import { observer } from 'mobx-react';
import type { FileResponseItem } from '../models.ts';
import { fileService } from '@services/fileService.ts';
import { Timestamp } from '@components/Timestamp.tsx';
import { ImgTrash } from '@components/Images/ImgTrash.tsx';
import { ImgRecycle } from '@components/Images/ImgRecycle.tsx';
import { ImgThreeDots } from '@components/Images/ImgThreeDots.tsx';
import { FileIcon } from '@components/FileIcon.tsx';
import ContextMenu from '@components/ContextMenu/ContextMenu.tsx';
import { ContextMenuItem } from '@components/ContextMenu/ContextMenuItem.tsx';
import { ContextMenuSeparator } from '@components/ContextMenu/ContextMenuSeparator.tsx';

type TrashItemMenuProps = {
  onRestore: () => void;
  onDelete: () => void;
};

const TrashItemMenu: React.FC<TrashItemMenuProps> = ({ onRestore, onDelete }) => {
  const btnRef = React.useRef<HTMLButtonElement>(null);
  return (
    <div style={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} className="action-btn file-opts-btn" title="Options" onClick={(e) => e.stopPropagation()}><ImgThreeDots /></button>
      <ContextMenu container={btnRef as React.RefObject<HTMLElement | null>} showOnClick>
        {() => (
          <div>
            <ContextMenuItem title={<><ImgRecycle /> Restore</>} onClick={onRestore} />
            <ContextMenuSeparator />
            <ContextMenuItem
              title={<><ImgTrash style={{ color: 'var(--danger-color)' }} /><span style={{ color: 'var(--danger-color)' }}>Delete Permanently</span></>}
              onClick={onDelete}
            />
          </div>
        )}
      </ContextMenu>
    </div>
  );
};

type TrashViewProps = {
  files: FileResponseItem[] | null;
  removingIds: Set<string>;
  onRestore: (file: FileResponseItem) => void;
  onDelete: (file: FileResponseItem) => void;
};

export const TrashView = observer(function TrashView({ files, removingIds, onRestore, onDelete }: TrashViewProps) {
  if (files === null) {
    return <div>Loading trash...</div>;
  }
  if (files.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', opacity: 0.6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><ImgTrash /> Trash is empty</div>;
  }
  return (
    <div className="file-list trash-file-list">
      <div className="file-list-header trash-list-header">
        <div>Name</div>
        <div>Auto-delete</div>
        <div>Actions</div>
      </div>
      {files.map(file => {
        const trashedAt = file.trashedAt ? new Date(file.trashedAt) : null;
        const autoDelete = trashedAt ? new Date(trashedAt.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
        return (
          <div className={`file-list-item trash-list-item${removingIds.has(file.id) ? ' removing' : ''}`} key={'t' + file.id}>
            <div className="file-item-name">
              <span className="file-icon file-icon-preview">
                <FileIcon fileName={file.fileName} fileId={file.id} file={file} />
              </span>
              <div className="file-list-item-content">
                <div className="file-list-item-row">
                  <span className="file-list-item-title">{file.fileName}</span>
                </div>
                <div className="file-list-item-row">
                  <span className="file-list-item-meta">{file.isFolder ? '—' : fileService.formatFileSize(file.fileSize)}</span>
                </div>
                <div className="file-list-item-row">
                  {trashedAt && <Timestamp timestamp={trashedAt} short />}
                </div>
              </div>
            </div>
            <div className="file-item-date" style={{ fontSize: '12px', opacity: 0.7 }}>
              {autoDelete && <Timestamp timestamp={autoDelete} short />}
            </div>
            <div className="file-item-actions">
              <TrashItemMenu
                onRestore={() => onRestore(file)}
                onDelete={() => onDelete(file)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
});
